import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError('');

    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setFormStatus('success');
      setSubmitting(false);
      return;
    }

    try {
      const formData = new FormData(form);
      if (honeypot) formData.delete('website_alt');

      const response = await fetch('https://readdy.ai/api/form/d9tcinn7mpqf8k8iifm0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg.includes('spam')) {
          setFormStatus('success');
        } else {
          setFormStatus('error');
          setFormError(serverMsg || '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 太阳娱乐 | 品牌介绍与联系方式"
        description="了解太阳娱乐品牌故事与发展历程。太阳娱乐是值得信赖的综合娱乐平台，拥有5000万+注册用户，提供安全稳定的娱乐服务。查看我们的口碑声誉与联系方式。"
        keywords="太阳娱乐,关于我们,品牌介绍,联系方式,太阳娱乐官网"
      />
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebPage" data={{
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于我们 - 太阳娱乐',
        url: 'https://hznaweisi.cn/about',
        description: '了解太阳娱乐品牌故事、发展历程与联系方式。',
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb />

          <section className="relative py-20 md:py-28 overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Elegant%20corporate%20headquarters%20lobby%20with%20warm%20golden%20lighting%2C%20sophisticated%20modern%20architecture%20with%20marble%20and%20brass%20accents%2C%20premium%20brand%20atmosphere%2C%20soft%20natural%20light%20streaming%20through%20large%20windows%2C%20professional%20and%20inviting%20corporate%20environment&width=1920&height=600&seq=about-hero-sun&orientation=landscape"
                alt="太阳娱乐品牌介绍"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
            </div>
            <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-6 text-center">
              <h1 className="text-3xl md:text-5xl font-bold text-background-50 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                关于太阳娱乐
              </h1>
              <p className="text-background-50/80 text-base md:text-lg leading-relaxed">
                值得信赖的综合娱乐平台，用专业与热情服务每一位用户
              </p>
            </div>
          </section>

          <section className="py-16 md:py-20 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <p className="text-sm font-medium text-primary-600 mb-2">品牌故事</p>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                    用专业铸就信赖，以品质赢得口碑
                  </h2>
                  <div className="space-y-4 text-foreground-700 leading-relaxed">
                    <p>
                      太阳娱乐成立于2018年，总部位于中国上海。自创立以来，我们始终秉持<strong>"用户至上、品质为先"</strong>的核心理念，致力于为广大用户提供安全、稳定、丰富的综合娱乐服务。
                    </p>
                    <p>
                      经过多年的深耕发展，太阳娱乐已成长为国内领先的综合娱乐平台，汇聚了电子竞技、娱乐资讯、体育赛事、精品游戏、影视推荐、音乐天地等多元化娱乐内容。我们拥有超过<strong>5000万注册用户</strong>，日均活跃用户达<strong>800万+</strong>，平台内容资源超过<strong>10万条</strong>。
                    </p>
                    <p>
                      太阳娱乐始终坚持技术驱动创新，拥有一支由行业资深专家组成的核心技术团队。平台采用国际领先的安全防护体系，确保用户数据安全与隐私保护。7×24小时全天候运营保障，让每一位用户都能享受到流畅、稳定的娱乐体验。
                    </p>
                    <p>
                      展望未来，太阳娱乐将继续深耕娱乐领域，不断拓展业务边界，以更加丰富的内容、更加优质的服务回馈广大用户的信任与支持。
                    </p>
                  </div>
                </div>
                <div className="rounded-2xl overflow-hidden aspect-[4/5]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20modern%20office%20team%20collaboration%20scene%2C%20diverse%20professionals%20working%20together%20in%20a%20bright%20and%20spacious%20environment%20with%20warm%20lighting%2C%20elegant%20corporate%20aesthetic%20with%20glass%20walls%20and%20indoor%20plants%2C%20genuine%20teamwork%20atmosphere&width=800&height=1000&seq=about-brand-sun&orientation=portrait"
                    alt="太阳娱乐团队"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </section>

          <section id="reputation" className="py-16 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <p className="text-sm font-medium text-primary-600 mb-2">口碑声誉</p>
                <h2 className="text-2xl md:text-4xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  用户与行业的共同认可
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-background-50 rounded-xl p-6 text-center border border-background-200">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                    <i className="ri-shield-check-line text-2xl text-primary-600"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-950 mb-2">安全认证</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    通过国际权威安全认证，采用银行级数据加密技术，全方位保障用户信息安全与资金安全。
                  </p>
                </div>
                <div className="bg-background-50 rounded-xl p-6 text-center border border-background-200">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                    <i className="ri-award-line text-2xl text-accent-600"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-950 mb-2">行业奖项</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    连续三年荣获"最佳综合娱乐平台"称号，多次获得行业权威机构颁发的优质服务认证和用户滿意度大奖。
                  </p>
                </div>
                <div className="bg-background-50 rounded-xl p-6 text-center border border-background-200">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-secondary-100 flex items-center justify-center">
                    <i className="ri-heart-line text-2xl text-secondary-600"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground-950 mb-2">用户口碑</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    5000万+用户的共同选择，平台满意度高达98.6%，用户推荐率行业领先。
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section id="contact" className="py-16 md:py-20 bg-background-50">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <p className="text-sm font-medium text-primary-600 mb-2">联系我们</p>
                <h2 className="text-2xl md:text-4xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  期待您的来信
                </h2>
                <p className="text-foreground-600 mt-2">
                  如有任何问题或建议，欢迎通过以下方式与我们取得联系
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                <div className="bg-background-100 rounded-xl p-5 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-primary-100 flex items-center justify-center">
                    <i className="ri-mail-line text-lg text-primary-600"></i>
                  </div>
                  <p className="text-xs text-foreground-500 mb-1">电子邮箱</p>
                  <p className="text-sm font-medium text-foreground-900">kefu@taiyangyule.com</p>
                </div>
                <div className="bg-background-100 rounded-xl p-5 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-accent-100 flex items-center justify-center">
                    <i className="ri-phone-line text-lg text-accent-600"></i>
                  </div>
                  <p className="text-xs text-foreground-500 mb-1">客服热线</p>
                  <p className="text-sm font-medium text-foreground-900">400-888-8888</p>
                </div>
                <div className="bg-background-100 rounded-xl p-5 text-center">
                  <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-secondary-100 flex items-center justify-center">
                    <i className="ri-time-line text-lg text-secondary-600"></i>
                  </div>
                  <p className="text-xs text-foreground-500 mb-1">服务时间</p>
                  <p className="text-sm font-medium text-foreground-900">7×24小时全天候</p>
                </div>
              </div>

              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="bg-background-100 rounded-2xl p-6 md:p-8 border border-background-200"
              >
                {formStatus === 'success' ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                      <i className="ri-check-line text-2xl text-primary-600"></i>
                    </div>
                    <h3 className="text-lg font-semibold text-foreground-950 mb-2">提交成功</h3>
                    <p className="text-sm text-foreground-600">感谢您的来信，我们的客服团队将尽快与您取得联系！</p>
                    <button
                      type="button"
                      onClick={() => setFormStatus('idle')}
                      className="mt-6 px-6 py-2.5 rounded-lg text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
                    >
                      继续留言
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">姓名</label>
                        <input
                          type="text"
                          id="contact-name"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">邮箱</label>
                        <input
                          type="email"
                          id="contact-email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>

                    <div className="mb-4">
                      <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">主题</label>
                      <input
                        type="text"
                        id="contact-subject"
                        name="subject"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入主题"
                      />
                    </div>

                    <div className="mb-4">
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">留言内容</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        rows={5}
                        maxLength={500}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言（最多500字）"
                      ></textarea>
                    </div>

                    <input
                      type="text"
                      name="website_alt"
                      className="honeypot-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />

                    {formStatus === 'error' && (
                      <div className="mb-4 p-3 rounded-lg bg-accent-100 text-accent-700 text-sm">
                        {formError}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-3 rounded-lg text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 cursor-pointer whitespace-nowrap"
                    >
                      {submitting ? '提交中...' : '发送留言'}
                    </button>
                  </>
                )}
              </form>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}